import { useNavigate } from "react-router";
import { useLeftMenu } from "../../store/useLeftMenu";
import { useAuth } from "../../store/useAuth";
import { Fragment } from "react/jsx-runtime";

interface Props {
  SvgIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  icon?: string;
  className?: string;
  /** Use dark text (for light backgrounds: lichtblauw, zalm) */
  lightBackground?: boolean;
  label: string;
  subLabel?: string;
  path: string;
  subPath?: string;
  subMenus?: Array<{
    userRoles?: string[];
    label: string;
    path: string;
    SvgIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
    icon?: string;
    subMenus?: Array<{
      userRoles: string[];
      label: string;
      path: string;
      SvgIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
      icon?: string;
    }>;
  }>;
  userRoles?: string[];
}

export function MenuItem({
  SvgIcon,
  icon,
  className,
  lightBackground,
  label,
  path,
  subPath,
  subMenus,
  userRoles,
}: Props) {
  /** Hooks */
  const { user } = useAuth();
  const { setIsOpen } = useLeftMenu();
  const navigate = useNavigate();

  const userRole = String(user?.user_role ?? 1);

  const pathname = window.location.pathname;
  const isActive = (pathname === "/login" && path === "/") || pathname === path;
  const isSubActive =
    pathname === subPath || subMenus?.some((menu) => menu.path === pathname);

  const isSubSubActive = subMenus?.some((menu) =>
    menu?.subMenus?.some((menu) => menu.path === pathname)
  );

  const nav = () => {
    setIsOpen(false);
    navigate(path);
  };

  // Selected state: orange stands out on all backgrounds
  const activeColor = "bg-droppie-oranje active:bg-droppie-oranje-dark";
  const inActiveColor = lightBackground
    ? "active:bg-black/10 hover:bg-black/10"
    : "active:bg-white/20 hover:bg-white/20";
  const textColor = lightBackground ? "text-black" : "text-white";

  if (!userRoles?.includes(userRole)) return null;

  return (
    <>
      <div
        onClick={nav}
        className={`outline-none w-full px-4 py-3 rounded-lg transition ease-linear ${
          isActive && !isSubActive ? activeColor : inActiveColor
        } flex justify-start align-middle cursor-pointer ${className} [&_svg]:fill-black`}
      >
        {SvgIcon ? <SvgIcon className="mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0" /> : null}
        {icon ? (
          <img
            alt="menu-icon"
            src={icon}
            className="w-[18px] h-[18px] mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0 brightness-0"
          />
        ) : null}
        <div
          className={`font-inter font-medium pb-0 pt-0 mb-0 mt-0 ${
            isActive && !isSubActive ? "text-white" : textColor
          }`}
        >
          {label}
        </div>
      </div>
      {subMenus && (isActive || isSubActive || isSubSubActive)
        ? subMenus?.map((menu) => {
            const isSubSubActive = menu?.subMenus?.some(
              (menu) => menu.path === pathname
            );
            if (!menu?.userRoles?.includes(userRole)) return null;
            return (
              <Fragment key={menu.path}>
                <div
                  onClick={() => {
                    setIsOpen(false);
                    navigate(menu.path);
                  }}
                  className={`outline-none pl-10 w-full px-4 py-3 rounded-lg transition ease-linear ${
                    pathname === menu.path ? activeColor : inActiveColor
                  } flex justify-start align-middle cursor-pointer ${className} [&_svg]:fill-black`}
                >
                  {menu?.SvgIcon ? (
                    <menu.SvgIcon className="mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0" />
                  ) : null}
                  {menu?.icon ? (
                    <img
                      alt="menu-icon"
                      src={menu?.icon}
                      className="w-[18px] h-[18px] mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0 brightness-0"
                    />
                  ) : null}
                  {!menu?.SvgIcon && !menu?.icon && SvgIcon ? (
                    <SvgIcon className="mr-2 pb-0 pt-0 mb-0 mt-1" />
                  ) : null}
                  {!menu?.icon && !menu?.SvgIcon && icon ? (
                    <img
                      alt="menu-icon"
                      src={icon}
                      className="w-[18px] h-[18px] mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0 brightness-0"
                    />
                  ) : null}
                  <div
                    className={`font-inter text-base font-medium pb-0 pt-0 mb-0 mt-0 ${
                      pathname === menu.path ? "text-white" : textColor
                    }`}
                  >
                    {menu?.label}
                  </div>
                </div>
                {menu?.subMenus && (pathname === menu.path || isSubSubActive)
                  ? menu?.subMenus?.map((subMenu) => {
                      if (!subMenu?.userRoles?.includes(userRole)) return null;
                      return (
                        <div
                          key={subMenu.path}
                          onClick={() => {
                            setIsOpen(false);
                            navigate(subMenu.path);
                          }}
                          className={`outline-none pl-20 w-full px-1 py-3 rounded-lg transition ease-linear ${
                            pathname === subMenu.path
                              ? activeColor
                              : inActiveColor
                          } flex justify-start align-middle cursor-pointer ${className} [&_svg]:fill-black`}
                        >
                          {subMenu?.SvgIcon ? (
                            <subMenu.SvgIcon className="mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0" />
                          ) : null}
                          {subMenu?.icon ? (
                            <img
                              alt="menu-icon"
                              src={subMenu?.icon}
                              className="w-[18px] h-[18px] mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0 brightness-0"
                            />
                          ) : null}
                          {!subMenu?.SvgIcon && !subMenu?.icon && SvgIcon ? (
                            <SvgIcon className="mr-2 pb-0 pt-0 mb-0 mt-1" />
                          ) : null}
                          {!subMenu?.icon && !subMenu?.SvgIcon && icon ? (
                            <img
                              alt="menu-icon"
                              src={icon}
                              className="w-[18px] h-[18px] mr-2 pb-0 pt-0 mb-0 mt-1 shrink-0 brightness-0"
                            />
                          ) : null}
                          <div
                            className={`font-inter font-medium text-base pb-0 pt-0 mb-0 mt-0 ${
                              pathname === subMenu.path ? "text-white" : textColor
                            }`}
                          >
                            {subMenu?.label}
                          </div>
                        </div>
                      );
                    })
                  : null}
              </Fragment>
            );
          })
        : null}
    </>
  );
}
